import React, { useCallback, useEffect, useRef, useState } from 'react';
import './ColorPicker.css';

interface ColorPickerProps {
  /** Current color value (hex string, e.g. "#ff0000") */
  value: string;
  /** Called when the color changes */
  onChange: (color: string) => void;
  /** Whether to show the hex value next to the swatch */
  showValue?: boolean;
  /** Retained for backwards-compatible call sites. */
  presetColors?: string[];
  /** Retained for backwards-compatible call sites. */
  pickerWidth?: string;
  /** Custom className for the wrapper */
  className?: string;
}

const FALLBACK_COLOR = '#ffffff';
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;
const COLOR_CHANGE_DELAY_MS = 150;

const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  showValue = false,
  className,
}) => {
  const colorValue = HEX_COLOR_PATTERN.test(value) ? value : FALLBACK_COLOR;
  const [draftColor, setDraftColor] = useState<string>(colorValue);
  const pendingColorRef = useRef<string | null>(null);
  const commitTimerRef = useRef<number | null>(null);

  const commitPendingColor = useCallback((): void => {
    if (commitTimerRef.current !== null) {
      window.clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }

    const pendingColor = pendingColorRef.current;
    pendingColorRef.current = null;
    if (pendingColor !== null && pendingColor !== value) {
      onChange(pendingColor);
    }
  }, [onChange, value]);

  useEffect(() => {
    setDraftColor(colorValue);
  }, [colorValue]);

  useEffect(() => () => {
    if (commitTimerRef.current !== null) {
      window.clearTimeout(commitTimerRef.current);
    }
  }, []);

  const handleColorInput = (nextColor: string): void => {
    setDraftColor(nextColor);
    pendingColorRef.current = nextColor;

    if (commitTimerRef.current !== null) {
      window.clearTimeout(commitTimerRef.current);
    }
    commitTimerRef.current = window.setTimeout(commitPendingColor, COLOR_CHANGE_DELAY_MS);
  };

  const wrapperClass = showValue
    ? `color-picker color-picker-with-value ${className ?? ''}`
    : `color-picker ${className ?? ''}`;

  return (
    <div className={wrapperClass.trim()}>
      <input
        className="color-picker-swatch"
        type="color"
        value={draftColor}
        onInput={(event) => handleColorInput(event.currentTarget.value)}
        onChange={(event) => handleColorInput(event.currentTarget.value)}
        onBlur={commitPendingColor}
        aria-label="Pick color"
      />

      {showValue && <span className="color-picker-value">{draftColor}</span>}
    </div>
  );
};

export default ColorPicker;

